import { useQuery, keepPreviousData } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { EventStatusType } from "@features/events/types";
import type { Payment } from "@features/events/types";

// Fetch events with optional status filtering, sorting, and limit
export function useEvents(
  hcc?: string, 
  status?: EventStatusType[] | null,
  sort?: string,
  limit?: number
) {
  const { GET } = useApiRequest();
  return useQuery({
    queryKey: ["events-dashboard-events", hcc, status, sort, limit],
    queryFn: async () => {
      if (!hcc) return [];

      const url = new URL(`/api/healthcare-companies/${hcc}/e/events`, window.location.origin);
      const params: Record<string, string> = {};
      
      if (status && status.length > 0) {
        params.status = status.join(",");
      }
      if (sort) {
        params.sort = sort;
      }
      if (limit) {
        params.limit = limit.toString();
      }

      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });

      return GET(url.pathname + url.search);
    },
    enabled: Boolean(hcc),
    placeholderData: keepPreviousData,
  });
}

// Fetch latest attendees, sorted by created_at desc
export function useAttendees(hcc?: string, limit?: number) {
  const { GET } = useApiRequest();
  return useQuery({
    queryKey: ["events-dashboard-attendees", hcc, limit],
    queryFn: async () => {
      if (!hcc) return [];
      
      const url = new URL(`/api/healthcare-companies/${hcc}/e/attendees`, window.location.origin);
      const params: Record<string, string> = {};
      
      if (limit) {
        params.limit = limit.toString();
      }

      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });

      return GET(url.pathname + url.search);
    },
    enabled: Boolean(hcc),
    placeholderData: keepPreviousData,
  });
}

// Fetch latest transactions, sorted by created_at desc
export function useTransactions(hcc?: string, limit?: number) {
  const { GET } = useApiRequest();
  return useQuery<Payment[]>({
    queryKey: ["events-dashboard-transactions", hcc, limit],
    queryFn: async () => {
      if (!hcc) return [];

      const url = new URL(
        `/api/healthcare-companies/${hcc}/e/finance/transactions`,
        window.location.origin
      );
      const params: Record<string, string> = {};

      if (limit) {
        params.limit = limit.toString();
      }

      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });

      return GET(url.pathname + url.search);
    },
    enabled: Boolean(hcc),
    placeholderData: keepPreviousData,
  });
}
